import routes from '@/commons/routes';
import { FormBuilder } from '@components/FormBuilder';
import { ScriptTagsWarning } from '@components/ScriptTagsWarning';
import { TemplateInfoAlert } from '@components/TemplateInfoAlert';
import { CodeEditor } from '@components/CodeEditor';
import {
  Button,
  Center,
  Container,
  Flex,
  Group,
  Loader,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useAppDispatch, useAppSelector } from '@store';
import {
  selectAllFolders,
  selectFoldersLoading,
} from '@store/selectors/foldersSelectors';
import { fetchFolderById, updateFolderFile } from '@store/slices/foldersSlice';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';

export const EditFileStructure = () => {
  const navigate = useNavigate();
  const { username, folderId, fileId } = useParams();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const folders = useAppSelector(selectAllFolders);
  const isLoading = useAppSelector(selectFoldersLoading);
  const [fields, setFields] = useState<any[]>([]);
  const [templateValue, setTemplateValue] = useState<string>('');

  const form: any = useForm({
    mode: 'uncontrolled',
    initialValues: {
      template: '',
      fields: [],
    },
  });

  // Find folder and file from Redux store
  const folder = useMemo(
    () => folders.find((f: any) => f.id === folderId),
    [folders, folderId]
  );

  const file = useMemo(
    () => folder?.files.find((f: any) => f.id === fileId),
    [folder, fileId]
  );

  useLayoutEffect(() => {
    if (!folderId) {
      return;
    }
    void dispatch(fetchFolderById(folderId));
  }, [dispatch, folderId]);

  // Initialize form when file is found
  useEffect(() => {
    if (file) {
      const fileFields = file.fields || [];
      const fileTemplate = file.template || '';
      setFields(fileFields);
      setTemplateValue(fileTemplate);
      form.setValues({
        template: fileTemplate,
        fields: fileFields,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.id]);

  // Handle errors when folder or file is not found (after load finishes)
  useEffect(() => {
    if (isLoading || !folderId) {
      return;
    }
    if (!folder) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({ id: 'folderNotFound' }),
        color: 'red',
      });
      navigate(routes.routes.userFolders(username as string));
      return;
    }
    if (!file) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({ id: 'fileNotFound' }),
        color: 'red',
      });
      navigate(routes.routes.userFolders(username as string));
    }
  }, [folder, file, isLoading, folderId, navigate, username, intl]);

  const handleSubmit = async (data: any) => {
    try {
      // Update via Redux
      await dispatch(
        updateFolderFile({
          folderId: folderId as string,
          fileId: fileId as string,
          updates: {
            template: data.template,
            fields: fields,
          },
        })
      ).unwrap();

      notifications.show({
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: 'fileStructureUpdated' },
          { name: file.name }
        ),
        color: 'green',
      });

      navigate(-1);
    } catch (err: any) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: err?.message || intl.formatMessage({ id: 'error' }),
        color: 'red',
      });
    }
  };

  const handleFieldsChange = useCallback(
    (newFields: any[]) => {
      setFields(newFields);
      form.setFieldValue('fields', newFields);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Handle template change
  const handleTemplateChange = (value: string) => {
    setTemplateValue(value);
    form.setFieldValue('template', value);
  };

  if (isLoading || !file || !folder) {
    return (
      <Container size="xl">
        <Center h={250}>
          <Loader />
        </Center>
      </Container>
    );
  }

  return (
    <Container my="md" size="xl">
      <Title order={4} mb="md">
        {intl.formatMessage({ id: 'editFileStructure' })} - {file.name}
      </Title>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Flex gap="md" direction="column">
          <FormBuilder fields={fields} onFieldsChange={handleFieldsChange} />

          <TemplateInfoAlert fields={fields} />

          <ScriptTagsWarning template={templateValue} />

          <CodeEditor
            value={templateValue}
            onChange={handleTemplateChange}
            label={intl.formatMessage({ id: 'template' })}
            placeholder={intl.formatMessage({ id: 'template' })}
          />

          <Group justify="space-between" my="xl">
            <Button variant="default" onClick={() => navigate(-1)}>
              {intl.formatMessage({ id: 'back' })}
            </Button>

            <Button type="submit" variant="filled">
              {intl.formatMessage({ id: 'update' })}
            </Button>
          </Group>
        </Flex>
      </form>
    </Container>
  );
};
